import moment from 'moment';
import clone from 'fast-copy';
import { Model } from '../Model.js';
import { ShipmentUpsales } from './ShipmentUpsales.js';
import { ShipmentCollection } from './ShipmentCollection.js';
import { ShipmentPrintInStore } from './ShipmentPrintInStore.js';
import equals from 'fast-deep-equal';
import { ShipmentService } from './ShipmentService.js';
import { Parcel } from '../Parcel/Parcel.js';
import { PriceMapper } from '../Price/PriceMapper.js';
import { Address } from '../Address/Address.js';
import { Content } from '../Content/Content.js';
import { ShipmentThirdPartyPreferences } from './ShipmentThirdPartyPreferences.js';

var _dec, _class;

/**
 * Plain object Shipment representation.
 *
 */

let IShipmentCustomsStatus = /*#__PURE__*/function (IShipmentCustomsStatus) {
  IShipmentCustomsStatus["DRAFT"] = "DRAFT";
  IShipmentCustomsStatus["COMPLETED"] = "COMPLETED";
  return IShipmentCustomsStatus;
}({});

/**
 * Serialized object Shipment representation.
 *
 */

/**
 * Plain object Packlink's API POST payload representation.
 *
 */

/**
 * Plain object Packlink's API POST cancel shipment payload representation.
 *
 */

/**
 * Plain object Packlink's API PUT payload representation.
 *
 */

function createCustomsModelFromResponse(reference) {
  return reference ? {
    customsInvoiceId: reference.customs_invoice_id,
    status: reference.status
  } : undefined;
}

/**
 * Plain object Packlink's API response representation.
 *
 */

/**
 * Plain object Packlink's API POST response.
 *
 */

/**
 * Plain object Packlink's API DELETE shipments response.
 *
 */

/**
 * Plain object Packlink's API shipment/getLabels response
 *
 */

/**
 * Plain object Packlink's API PUT multiple shipments response
 *
 */

/**
 * Plain object for multiple shipments response representation
 *
 */

let Shipment = (_dec = Model(), _dec(_class = class Shipment {
  /**
   * Takes a IShipmentApiResponse from Packlink's API and returns a Shipment.
   *
   * @param response  IShipmentApiResponse response from Packlink's API.
   *
   */
  static createFromResponse(response) {
    var _response$priority, _response$third_party, _response$third_party2, _response$third_party3, _response$is_obfuscat;
    return new Shipment(response.additional_data, response.currency, response.labels === null ? undefined : response.labels, response.packlink_reference, response.shipment_custom_reference || undefined, response.state, response.trackings === null ? undefined : response.trackings, response.tracking_url || undefined, response.dropoff_point_id || undefined, response.carrier_product_id || undefined, response.source, moment(response.order_date, 'YYYY/MM/DD').hour(12), response.order_reference || undefined, response.label_downloaded, response.estimated_delivery_date ? moment(response.estimated_delivery_date, 'YYYY/MM/DD').hour(12) : undefined, response.packages.map(Parcel.createFromResponse), PriceMapper.createModelFromResponse(generatePriceApiResponse(response)), Address.createFromResponse(response.from), Address.createFromResponse(response.to), ShipmentService.createFromResponse({
      carrier: response.carrier,
      service: response.service,
      service_id: response.service_id,
      service_info: response.service_info
    }), ShipmentCollection.createFromResponse({
      collection_date: response.collection_date,
      collection_time: response.collection_time
    }), new Content(response.content || undefined, response.contentvalue || undefined, response.content_second_hand), ShipmentUpsales.createFromResponse({
      adult_signature: response.adult_signature,
      additional_handling: response.additional_handling,
      cash_on_delivery: response.cash_on_delivery,
      insurance: response.insurance,
      print_in_store_selected: response.print_in_store_selected,
      proof_of_delivery: response.proof_of_delivery,
      priority: (_response$priority = response.priority) !== null && _response$priority !== void 0 ? _response$priority : false
    }), response.print_in_store ? ShipmentPrintInStore.createFromResponse(response.print_in_store) : undefined, response.voucher_name, response.parent_reference, createCustomsModelFromResponse(response.customs), response.has_customs, response.purchase_date ? moment.utc(response.purchase_date, 'YYYY/MM/DD hh:mm:ss') : undefined, response.third_party_preferences ? ShipmentThirdPartyPreferences.createFromResponse({
      carrier: response === null || response === void 0 ? void 0 : (_response$third_party = response.third_party_preferences) === null || _response$third_party === void 0 ? void 0 : _response$third_party.carrier,
      service_id: (_response$third_party2 = response.third_party_preferences) === null || _response$third_party2 === void 0 ? void 0 : _response$third_party2.service_id,
      drop_off_point_id: response === null || response === void 0 ? void 0 : (_response$third_party3 = response.third_party_preferences) === null || _response$third_party3 === void 0 ? void 0 : _response$third_party3.drop_off_point_id
    }) : undefined, (_response$is_obfuscat = response.is_obfuscated) !== null && _response$is_obfuscat !== void 0 ? _response$is_obfuscat : false);
  }

  /**
   * Takes a serialized Shipment and returns a Shipment.
   *
   * It allows you to hint the type of data via type params.
   *
   * @typeparam U  Type of the deserialized Shipment.
   * This allows apps using the SDK to provide their own serialization and deserialization types.
   * By default equals to SerializedShipment.
   * @typeparam P  Type of the serialized data. By default equals to `U`.
   *
   * @param data  Serialized representation of Shipment.
   *
   */

  /**
   * Takes a serialized Shipment and returns a Shipment.
   *
   * It allows you to hint the type of data via type params.
   *
   * @typeparam U  Type of the deserialized Shipment.
   * This allows apps using the SDK to provide their own serialization and deserialization types.
   * By default equals to SerializedShipment.
   * @typeparam P  Type of the serialized data. By default equals to `U`.
   *
   * @param data  Serialized representation of Shipment.
   *
   */

  /**
   * Takes a serialized Shipment and returns a Shipment.
   *
   * It allows you to hint the type of data via type params.
   *
   * @typeparam U  Type of the deserialized Shipment.
   * This allows apps using the SDK to provide their own serialization and deserialization types.
   * By default equals to SerializedShipment.
   * @typeparam P  Type of the serialized data. By default equals to `U`.
   *
   * @param data  Serialized representation of Shipment.
   *
   */

  /**
   * Takes a serialized Shipment and returns a Shipment.
   *
   * It allows you to hint the type of data via type params.
   *
   * @typeparam U  Type of the deserialized Shipment.
   * This allows apps using the SDK to provide their own serialization and deserialization types.
   * By default equals to SerializedShipment.
   * @typeparam P  Type of the serialized data. By default equals to `U`.
   *
   * @param data  Serialized representation of Shipment.
   *
   */
  static deserialize(data) {
    var _data$parcels;
    if (!data) {
      return;
    }
    return new Shipment(data.additionalData, data.currency, data.labels, data.packlinkReference, data.shipmentCustomReference, data.state, data.trackings, data.trackingUrl, data.dropoffPointId, data.carrierProductId, data.source, moment(data.orderDate, 'YYYY/MM/DD').hour(12), data.orderReference, data.labelDownloaded, data.estimatedDeliveryDate ? moment(data.estimatedDeliveryDate, 'YYYY/MM/DD').hour(12) : undefined, (_data$parcels = data.parcels) === null || _data$parcels === void 0 ? void 0 : _data$parcels.map(parcel => Parcel.deserialize(parcel)), clone(data.price), Address.deserialize(data.from), Address.deserialize(data.to), ShipmentService.deserialize(data.service), ShipmentCollection.deserialize(data.collection), Content.deserialize(data.content), ShipmentUpsales.deserialize(data.upsales), ShipmentPrintInStore.deserialize(data.printInStore), data.voucherName, data.parentReference, data.customs, data.hasCustoms, data.purchaseDate ? moment(data.purchaseDate) : undefined, ShipmentThirdPartyPreferences.deserialize(data.thirdPartyPreferences), data.isObfuscated);
  }

  /**
   * Returns a new Shipment. Typings of the properties depend on the class type param T
   *
   */
  constructor(additionalData, currency, labels, packlinkReference, shipmentCustomReference, state, trackings, trackingUrl, dropoffPointId, carrierProductId, source, orderDate, orderReference, labelDownloaded, estimatedDeliveryDate, parcels, price, from, to, service, collection, content, upsales, printInStore, voucherName, parentReference, customs, hasCustoms, purchaseDate, thirdPartyPreferences, isObfuscated) {
    this.additionalData = additionalData;
    this.currency = currency;
    this.labels = labels;
    this.packlinkReference = packlinkReference;
    this.shipmentCustomReference = shipmentCustomReference;
    this.state = state;
    this.trackings = trackings;
    this.trackingUrl = trackingUrl;
    this.dropoffPointId = dropoffPointId;
    this.carrierProductId = carrierProductId;
    this.source = source;
    this.orderDate = orderDate;
    this.orderReference = orderReference;
    this.labelDownloaded = labelDownloaded;
    this.estimatedDeliveryDate = estimatedDeliveryDate;
    this.parcels = parcels;
    this.price = price;
    this.from = from;
    this.to = to;
    this.service = service;
    this.collection = collection;
    this.content = content;
    this.upsales = upsales;
    this.printInStore = printInStore;
    this.voucherName = voucherName;
    this.parentReference = parentReference;
    this.customs = customs;
    this.hasCustoms = hasCustoms;
    this.purchaseDate = purchaseDate;
    this.thirdPartyPreferences = thirdPartyPreferences;
    this.isObfuscated = isObfuscated;
  }

  /**
   * Returns a JSON serialized version of the Shipment.
   *
   */
  toJSON() {
    var _this$collection, _this$content, _this$estimatedDelive, _this$from, _this$parcels, _this$printInStore, _this$service, _this$to, _this$upsales, _this$purchaseDate;
    return {
      additionalData: this.additionalData,
      thirdPartyPreferences: this.thirdPartyPreferences,
      carrierProductId: this.carrierProductId,
      collection: (_this$collection = this.collection) === null || _this$collection === void 0 ? void 0 : _this$collection.toJSON(),
      content: (_this$content = this.content) === null || _this$content === void 0 ? void 0 : _this$content.toJSON(),
      currency: this.currency,
      dropoffPointId: this.dropoffPointId,
      estimatedDeliveryDate: (_this$estimatedDelive = this.estimatedDeliveryDate) === null || _this$estimatedDelive === void 0 ? void 0 : _this$estimatedDelive.toISOString(),
      from: (_this$from = this.from) === null || _this$from === void 0 ? void 0 : _this$from.toJSON(),
      labels: this.labels,
      labelDownloaded: this.labelDownloaded,
      orderDate: this.orderDate.toISOString(),
      orderReference: this.orderReference,
      packlinkReference: this.packlinkReference,
      parcels: (_this$parcels = this.parcels) === null || _this$parcels === void 0 ? void 0 : _this$parcels.map(element => element.toJSON()),
      price: this.price,
      printInStore: (_this$printInStore = this.printInStore) === null || _this$printInStore === void 0 ? void 0 : _this$printInStore.toJSON(),
      service: (_this$service = this.service) === null || _this$service === void 0 ? void 0 : _this$service.toJSON(),
      shipmentCustomReference: this.shipmentCustomReference,
      source: this.source,
      state: this.state,
      to: (_this$to = this.to) === null || _this$to === void 0 ? void 0 : _this$to.toJSON(),
      trackingUrl: this.trackingUrl,
      trackings: this.trackings,
      upsales: (_this$upsales = this.upsales) === null || _this$upsales === void 0 ? void 0 : _this$upsales.toJSON(),
      voucherName: this.voucherName,
      parentReference: this.parentReference,
      customs: this.customs,
      hasCustoms: this.hasCustoms,
      purchaseDate: (_this$purchaseDate = this.purchaseDate) === null || _this$purchaseDate === void 0 ? void 0 : _this$purchaseDate.toISOString(),
      isObfuscated: this.isObfuscated
    };
  }

  /**
   * Returns a clone of the Shipment.
   *
   */
  clone() {
    var _this$parcels2, _this$from2, _this$to2, _this$service2, _this$collection2, _this$content2, _this$upsales2, _this$printInStore2;
    return new Shipment(this.additionalData, this.currency, this.labels, this.packlinkReference, this.shipmentCustomReference, this.state, this.trackings, this.trackingUrl, this.dropoffPointId, this.carrierProductId, this.source, this.orderDate, this.orderReference, this.labelDownloaded, this.estimatedDeliveryDate, (_this$parcels2 = this.parcels) === null || _this$parcels2 === void 0 ? void 0 : _this$parcels2.map(element => element.clone()), clone(this.price), (_this$from2 = this.from) === null || _this$from2 === void 0 ? void 0 : _this$from2.clone(), (_this$to2 = this.to) === null || _this$to2 === void 0 ? void 0 : _this$to2.clone(), (_this$service2 = this.service) === null || _this$service2 === void 0 ? void 0 : _this$service2.clone(), (_this$collection2 = this.collection) === null || _this$collection2 === void 0 ? void 0 : _this$collection2.clone(), (_this$content2 = this.content) === null || _this$content2 === void 0 ? void 0 : _this$content2.clone(), (_this$upsales2 = this.upsales) === null || _this$upsales2 === void 0 ? void 0 : _this$upsales2.clone(), (_this$printInStore2 = this.printInStore) === null || _this$printInStore2 === void 0 ? void 0 : _this$printInStore2.clone(), this.voucherName, this.parentReference, this.customs, this.hasCustoms, this.purchaseDate, this.thirdPartyPreferences, this.isObfuscated);
  }

  /**
   * Returns whether the two Shipment are equal.
   *
   */
  equals(shipment) {
    var _this$from3, _this$labels, _this$parcels3, _this$to3, _this$trackings, _this$service3, _this$collection3, _this$content3, _this$upsales3, _this$printInStore3, _this$estimatedDelive2, _shipment$estimatedDe, _this$purchaseDate2, _shipment$purchaseDat, _this$customs, _shipment$customs, _this$customs2, _shipment$customs2;
    if (!shipment) {
      return false;
    }
    return this.additionalData === shipment.additionalData && this.currency === shipment.currency && !!((_this$from3 = this.from) !== null && _this$from3 !== void 0 && _this$from3.equals(shipment.from)) && ((_this$labels = this.labels) === null || _this$labels === void 0 ? void 0 : _this$labels.find((label, key) => {
      var _shipment$labels;
      return label !== ((_shipment$labels = shipment.labels) === null || _shipment$labels === void 0 ? void 0 : _shipment$labels[key]);
    })) === undefined && this.packlinkReference === shipment.packlinkReference && ((_this$parcels3 = this.parcels) === null || _this$parcels3 === void 0 ? void 0 : _this$parcels3.find((parcel, key) => {
      var _shipment$parcels;
      return !parcel.equals((_shipment$parcels = shipment.parcels) === null || _shipment$parcels === void 0 ? void 0 : _shipment$parcels[key]);
    })) === undefined && equals(this.price, shipment.price) && this.shipmentCustomReference === shipment.shipmentCustomReference && this.state === shipment.state && !!((_this$to3 = this.to) !== null && _this$to3 !== void 0 && _this$to3.equals(shipment.to)) && ((_this$trackings = this.trackings) === null || _this$trackings === void 0 ? void 0 : _this$trackings.find((tracking, key) => {
      var _shipment$trackings;
      return tracking !== ((_shipment$trackings = shipment.trackings) === null || _shipment$trackings === void 0 ? void 0 : _shipment$trackings[key]);
    })) === undefined && !!((_this$service3 = this.service) !== null && _this$service3 !== void 0 && _this$service3.equals(shipment.service)) && !!((_this$collection3 = this.collection) !== null && _this$collection3 !== void 0 && _this$collection3.equals(shipment.collection)) && this.trackingUrl === shipment.trackingUrl && !!((_this$content3 = this.content) !== null && _this$content3 !== void 0 && _this$content3.equals(shipment.content)) && !!((_this$upsales3 = this.upsales) !== null && _this$upsales3 !== void 0 && _this$upsales3.equals(shipment.upsales)) && !!((_this$printInStore3 = this.printInStore) !== null && _this$printInStore3 !== void 0 && _this$printInStore3.equals(shipment.printInStore)) && this.dropoffPointId === shipment.dropoffPointId && ((_this$estimatedDelive2 = this.estimatedDeliveryDate) === null || _this$estimatedDelive2 === void 0 ? void 0 : _this$estimatedDelive2.toISOString()) === ((_shipment$estimatedDe = shipment.estimatedDeliveryDate) === null || _shipment$estimatedDe === void 0 ? void 0 : _shipment$estimatedDe.toISOString()) && this.orderDate.toISOString() === shipment.orderDate.toISOString() && ((_this$purchaseDate2 = this.purchaseDate) === null || _this$purchaseDate2 === void 0 ? void 0 : _this$purchaseDate2.toISOString()) === ((_shipment$purchaseDat = shipment.purchaseDate) === null || _shipment$purchaseDat === void 0 ? void 0 : _shipment$purchaseDat.toISOString()) && this.orderReference === shipment.orderReference && this.labelDownloaded === shipment.labelDownloaded && this.carrierProductId === shipment.carrierProductId && this.source === shipment.source && this.voucherName === shipment.voucherName && this.parentReference === shipment.parentReference && ((_this$customs = this.customs) === null || _this$customs === void 0 ? void 0 : _this$customs.customsInvoiceId) === ((_shipment$customs = shipment.customs) === null || _shipment$customs === void 0 ? void 0 : _shipment$customs.customsInvoiceId) && ((_this$customs2 = this.customs) === null || _this$customs2 === void 0 ? void 0 : _this$customs2.status) === ((_shipment$customs2 = shipment.customs) === null || _shipment$customs2 === void 0 ? void 0 : _shipment$customs2.status) && this.hasCustoms === shipment.hasCustoms && this.thirdPartyPreferences === shipment.thirdPartyPreferences && this.isObfuscated === shipment.isObfuscated;
  }

  /**
   * Returns a JSON with the payload necessary to cancel a Shipment.
   *
   */
  getCancelPayload() {
    var _this$service4, _this$collection4, _this$from4, _this$from5, _this$from6;
    return {
      carrier: (_this$service4 = this.service) === null || _this$service4 === void 0 ? void 0 : _this$service4.carrierName,
      carrier_product_id: this.carrierProductId,
      collection_date: (_this$collection4 = this.collection) === null || _this$collection4 === void 0 ? void 0 : _this$collection4.getCreatePayload().collection_date,
      email: (_this$from4 = this.from) === null || _this$from4 === void 0 ? void 0 : _this$from4.email,
      name: `${(_this$from5 = this.from) === null || _this$from5 === void 0 ? void 0 : _this$from5.firstName} ${(_this$from6 = this.from) === null || _this$from6 === void 0 ? void 0 : _this$from6.lastName}`,
      packlink_reference: this.packlinkReference
    };
  }

  /**
   * Returns a JSON with the payload necessary to create a Shipment.
   *
   */
  getCreatePayload() {
    var _this$service5, _this$collection5, _this$upsales4, _this$content4, _this$content5, _this$content6, _this$from7, _this$parcels4, _this$to4;
    return {
      ...((_this$service5 = this.service) === null || _this$service5 === void 0 ? void 0 : _this$service5.getCreatePayload()),
      ...((_this$collection5 = this.collection) === null || _this$collection5 === void 0 ? void 0 : _this$collection5.getCreatePayload()),
      ...((_this$upsales4 = this.upsales) === null || _this$upsales4 === void 0 ? void 0 : _this$upsales4.getCreatePayload()),
      additional_data: this.additionalData,
      content: (_this$content4 = this.content) === null || _this$content4 === void 0 ? void 0 : _this$content4.description,
      content_second_hand: (_this$content5 = this.content) === null || _this$content5 === void 0 ? void 0 : _this$content5.secondHand,
      contentvalue: (_this$content6 = this.content) === null || _this$content6 === void 0 ? void 0 : _this$content6.value,
      currency: this.currency,
      dropoff_point_id: this.dropoffPointId,
      from: (_this$from7 = this.from) === null || _this$from7 === void 0 ? void 0 : _this$from7.getCreatePayload(),
      packages: (_this$parcels4 = this.parcels) === null || _this$parcels4 === void 0 ? void 0 : _this$parcels4.map(parcel => parcel.getCreatePayload()),
      shipment_custom_reference: this.shipmentCustomReference,
      to: (_this$to4 = this.to) === null || _this$to4 === void 0 ? void 0 : _this$to4.getCreatePayload(),
      voucher_name: this.voucherName,
      has_customs: this.hasCustoms,
      customs: this.customs ? {
        customs_invoice_id: this.customs.customsInvoiceId,
        status: this.customs.status
      } : undefined,
      third_party_preferences: this.thirdPartyPreferences
    };
  }

  /**
   * Returns a JSON with the payload of the shipment data to create an Order
   *
   */
  getCreateOrderPayload(voucher, customsInvoiceId) {
    var _this$price, _this$price$price, _this$price2, _this$price2$price, _this$price3, _this$price3$price;
    const updatePayload = this.getUpdatePayload();
    return {
      ...updatePayload,
      price: {
        base_price: ((_this$price = this.price) === null || _this$price === void 0 ? void 0 : (_this$price$price = _this$price.price) === null || _this$price$price === void 0 ? void 0 : _this$price$price.basePrice) || null,
        tax_price: ((_this$price2 = this.price) === null || _this$price2 === void 0 ? void 0 : (_this$price2$price = _this$price2.price) === null || _this$price2$price === void 0 ? void 0 : _this$price2$price.taxPrice) || null,
        total_price: ((_this$price3 = this.price) === null || _this$price3 === void 0 ? void 0 : (_this$price3$price = _this$price3.price) === null || _this$price3$price === void 0 ? void 0 : _this$price3$price.totalPrice) || null
      },
      ...(voucher && {
        voucher_name: voucher.name
      }),
      ...(customsInvoiceId && {
        customs: {
          customs_invoice_id: customsInvoiceId
        }
      })
    };
  }

  /**
   * Returns a JSON with the payload necessary to update a Shipment.
   *
   */
  getUpdatePayload() {
    var _this$service6, _this$collection6, _this$upsales5, _this$content7, _this$content8, _this$content9, _this$from8, _this$parcels5, _this$to5;
    return {
      ...((_this$service6 = this.service) === null || _this$service6 === void 0 ? void 0 : _this$service6.getCreatePayload()),
      ...((_this$collection6 = this.collection) === null || _this$collection6 === void 0 ? void 0 : _this$collection6.getCreatePayload()),
      ...((_this$upsales5 = this.upsales) === null || _this$upsales5 === void 0 ? void 0 : _this$upsales5.getCreatePayload()),
      additional_data: this.additionalData,
      content: (_this$content7 = this.content) === null || _this$content7 === void 0 ? void 0 : _this$content7.description,
      content_second_hand: (_this$content8 = this.content) === null || _this$content8 === void 0 ? void 0 : _this$content8.secondHand,
      contentvalue: (_this$content9 = this.content) === null || _this$content9 === void 0 ? void 0 : _this$content9.value,
      currency: this.currency,
      dropoff_point_id: this.dropoffPointId,
      from: (_this$from8 = this.from) === null || _this$from8 === void 0 ? void 0 : _this$from8.getUpdatePayload(),
      order_reference: this.orderReference,
      packages: (_this$parcels5 = this.parcels) === null || _this$parcels5 === void 0 ? void 0 : _this$parcels5.map(parcel => parcel.getCreatePayload()),
      packlink_reference: this.packlinkReference,
      shipment_custom_reference: this.shipmentCustomReference,
      to: (_this$to5 = this.to) === null || _this$to5 === void 0 ? void 0 : _this$to5.getUpdatePayload(),
      voucher_name: this.voucherName,
      has_customs: this.hasCustoms,
      ...(this.customs && {
        customs: {
          customs_invoice_id: this.customs.customsInvoiceId
        }
      }),
      third_party_preferences: this.thirdPartyPreferences
    };
  }
}) || _class);
const generatePriceApiResponse = shipmentApiResponse => {
  const {
    price,
    packlink_reference,
    currency
  } = shipmentApiResponse;
  return {
    packlink_reference,
    price: {
      base_price: price.base_price,
      tax_price: price.tax_price,
      total_price: price.total_price,
      currency,
      taxes: [{
        percentage: price.tax_price && price.base_price && Math.round(price.tax_price / price.base_price * 100) || 0
      }]
    },
    products: price.items.reduce((products, item) => {
      switch (item.concept) {
        case 'CARRIERSERVICE':
          products.porterage = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
        case 'INSURANCE':
          products.insurance = {
            assurance_cost_percent: item.extra_data.assurance_cost_percent,
            base_coverage: item.extra_data.base_coverage,
            base_price: item.base_price,
            insured: item.extra_data.insured,
            max_coverage: item.extra_data.max_coverage,
            packlink_coverage: item.extra_data.packlink_coverage,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
        case 'PROOF_OF_DELIVERY':
          products.proof_of_delivery = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
        case 'ADULT_SIGNATURE':
          products.adult_signature = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
        case 'ADDITIONAL_HANDLING':
          products.additional_handling = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
        case 'MANAGEMENT_FEE':
          products.management_fee = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
        case 'VOUCHER':
          products.voucher = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            name: item.extra_data.name,
            currency,
            percentage: item.extra_data.percentage
          };
          break;
        case 'CASH_ON_DELIVERY':
          products.cash_on_delivery = {
            base_price: item.base_price,
            tax_price: item.tax_price,
            total_price: item.total_price,
            currency
          };
          break;
      }
      return products;
    }, {
      porterage: {}
    })
  };
};

export { IShipmentCustomsStatus, Shipment, generatePriceApiResponse };
//# sourceMappingURL=Shipment.js.map
